import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Stack,
  Breadcrumbs,
  Link,
  IconButton,
} from "@mui/material";
import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import CategorySetupCard from "./components/CategorySetupCard";
import SubCategorySetupCard from "./components/SubCategorySetupCard";
import MenuItemsSetupCard from "./components/MenuItemsSetupCard";
import ItemVariationsSetupCard from "./components/ItemVariationsSetupCard";
import ModifiersSetupCard from "./components/ModifiersSetupCard";
import ModifierOptionsSetupCard from "./components/ModifierOptionsSetupCard";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import CategoryIcon from "@mui/icons-material/Category";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import TuneIcon from "@mui/icons-material/Tune";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const setupMenuItems = [
  {
    groupId: "menu",
    groupTitle: "Menu Management",
    groupIcon: <RestaurantMenuIcon fontSize="small" />,
    items: [
      {
        id: "category",
        title: "Categories",
        icon: <CategoryIcon fontSize="small" />,
      },
      {
        id: "subCategory",
        title: "Sub Categories",
        icon: <SubdirectoryArrowRightIcon fontSize="small" />,
      },
      {
        id: "menuItems",
        title: "Menu Items",
        icon: <MenuBookIcon fontSize="small" />,
      },
      {
        id: "itemVariations",
        title: "Item Variations",
        icon: <TuneIcon fontSize="small" />,
      },
      {
        id: "modifiers",
        title: "Modifiers",
        icon: <AddCircleOutlineIcon fontSize="small" />,
      },
      {
        id: "modifierOptions",
        title: "Modifier Options",
        icon: <TuneIcon fontSize="small" />,
      },
    ],
  },
];

const MenuDataSetup = () => {
  const [selectedCard, setSelectedCard] = useState("category");
  const [expandedGroups, setExpandedGroups] = useState(["menu"]);
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const handleGroupToggle = (groupId) => {
    setExpandedGroups((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleItemClick = (itemId) => {
    setSelectedCard(itemId);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumb Navigation */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton
          onClick={() => navigate("/settings")}
          sx={{
            background: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.03)",
            "&:hover": {
              background: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.06)",
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: theme.palette.text.secondary,
            }}
            onClick={() => navigate("/dashboard")}
          >
            <HomeIcon fontSize="small" />
            Dashboard
          </Link>
          <Link
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
              color: theme.palette.text.secondary,
            }}
            onClick={() => navigate("/settings")}
          >
            <SettingsIcon fontSize="small" />
            Settings
          </Link>
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              color: theme.palette.text.primary,
              fontWeight: 600,
            }}
          >
            Menu Settings
          </Typography>
        </Breadcrumbs>
      </Box>

      {/* Page Header */}
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            background: isDark
              ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.light} 100%)`
              : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            letterSpacing: "-0.5px",
          }}
        >
          Menu Settings
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: "0.95rem",
            lineHeight: 1.6,
            // maxWidth: "700px",
          }}
        >
          Configure and manage all your restaurant data in one place. Set up
          your menu, items, pricing, modifiers, tables, and promotional offers.
        </Typography>
      </Stack>

      {/* Main Content */}
      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 150px)",
          overflow: "hidden",
          gap: 3,
        }}
      >
        {/* Sidebar Navigation */}
        <Paper
          elevation={0}
          sx={{
            width: 280,
            flexShrink: 0,
            borderRadius: "16px",
            border: `1px solid ${
              isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"
            }`,
            background: isDark
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(10px)",
            overflowY: "auto",
            overflowX: "hidden",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)",
              borderRadius: "10px",
              "&:hover": {
                background: isDark
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          {/* Navigation List */}
          <List sx={{ py: 2, px: 1.5 }}>
            {setupMenuItems.map((group) => (
              <Box key={group.groupId}>
                {/* Group Header */}
                <ListItemButton
                  onClick={() => handleGroupToggle(group.groupId)}
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    py: 1,
                    "&:hover": {
                      background: isDark
                        ? "rgba(102, 126, 234, 0.1)"
                        : "rgba(102, 126, 234, 0.08)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box
                      sx={{
                        color: theme.palette.primary.main,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {group.groupIcon}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={group.groupTitle}
                    primaryTypographyProps={{
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                  />
                  {expandedGroups.includes(group.groupId) ? (
                    <ExpandLessIcon
                      fontSize="small"
                      sx={{ color: theme.palette.text.secondary }}
                    />
                  ) : (
                    <ExpandMoreIcon
                      fontSize="small"
                      sx={{ color: theme.palette.text.secondary }}
                    />
                  )}
                </ListItemButton>

                {/* Group Items */}
                <Collapse
                  in={expandedGroups.includes(group.groupId)}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {group.items.map((item) => {
                      const isActive = selectedCard === item.id;
                      return (
                        <ListItem key={item.id} disablePadding sx={{ pl: 1 }}>
                          <ListItemButton
                            selected={isActive}
                            onClick={() => handleItemClick(item.id)}
                            sx={{
                              borderRadius: "8px",
                              py: 1,
                              mb: 0.5,
                              position: "relative",
                              "&.Mui-selected": {
                                background: isDark
                                  ? `linear-gradient(135deg, ${theme.palette.primary.main}20 0%, ${theme.palette.secondary.main}15 100%)`
                                  : `linear-gradient(135deg, ${theme.palette.primary.main}12 0%, ${theme.palette.secondary.main}10 100%)`,
                                borderLeft: `3px solid ${theme.palette.primary.main}`,
                                "&:hover": {
                                  background: isDark
                                    ? `linear-gradient(135deg, ${theme.palette.primary.main}25 0%, ${theme.palette.secondary.main}20 100%)`
                                    : `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}12 100%)`,
                                },
                              },
                              "&:hover": {
                                background: isDark
                                  ? "rgba(255, 255, 255, 0.05)"
                                  : "rgba(0, 0, 0, 0.03)",
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, pl: 1 }}>
                              <Box
                                sx={{
                                  color: isActive
                                    ? theme.palette.primary.main
                                    : theme.palette.text.secondary,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {item.icon}
                              </Box>
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: "0.85rem",
                                fontWeight: isActive ? 600 : 500,
                                color: isActive
                                  ? theme.palette.primary.main
                                  : theme.palette.text.secondary,
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            ))}
          </List>
        </Paper>

        {/* Main Content Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            background: isDark
              ? "rgba(255, 255, 255, 0.02)"
              : "rgba(255, 255, 255, 0.6)",
            backdropFilter: "blur(10px)",
            borderRadius: "16px",
            border: `1px solid ${
              isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.08)"
            }`,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: isDark
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.2)",
              borderRadius: "10px",
              "&:hover": {
                background: isDark
                  ? "rgba(255, 255, 255, 0.3)"
                  : "rgba(0, 0, 0, 0.3)",
              },
            },
          }}
        >
          {/* Content Body */}
          <Box sx={{ p: 3 }}>
            {selectedCard === "category" && <CategorySetupCard />}
            {selectedCard === "subCategory" && <SubCategorySetupCard />}
            {selectedCard === "menuItems" && <MenuItemsSetupCard />}
            {selectedCard === "itemVariations" && <ItemVariationsSetupCard />}
            {selectedCard === "modifiers" && <ModifiersSetupCard />}
            {selectedCard === "modifierOptions" && <ModifierOptionsSetupCard />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MenuDataSetup;
